/**
 * Payments of the standalone web (docs/13 §5.8): prices from `/billing/products`, the trip to
 * Stripe and the wait for the webhook on the way back.
 *
 * Every amount on screen is computed here from `amountMinor`. There is no fallback price anywhere:
 * if the API does not answer, the screens show an error with a retry, never a number (§1).
 */
import { AkpError, type PriceDTO, type ProductDTO } from '@akpl/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

import { useAuth } from '../auth';
import { ME_QUERY_KEY } from '../hooks/useMe';
import { webApi } from './api';
import { useCardModalEnabled, useLocale, usePlans, useStripeKey, useWebT } from './hooks';
import { type Intent, saveIntent, useWebUi, type WebSong } from './store';

/**
 * Everything is charged up front, so the web offers no trial. Whatever promises one to a buyer hangs
 * from this flag and the `web:billing.trial.*` keys. What a user who is actually in a trial sees
 * (E-19) comes from `me.plan`, not from here.
 */
export const TRIAL_OFFER_ENABLED = false;

export type PlanInterval = 'month' | 'year';

export interface PlanPrice {
  priceId: string;
  interval: PlanInterval;
  amountMinor: number;
  currency: string;
  /** `amountMinor` formatted for the locale. */
  amount: string;
}

export interface PlanOffer {
  month: PlanPrice | null;
  year: PlanPrice | null;
  /** Null when there is nothing to compare against, or the yearly price saves nothing. */
  savingsPercent: number | null;
  /** The yearly price per month. */
  monthlyEquivalent: string | null;
  /** The free plan in the same currency as the paid ones. */
  freeAmount: string;
}

/**
 * The copy is Latin American Spanish ("celular", "computadora"): plain `es` puts the symbol after the amount,
 * while `es-419` prints it first with a decimal point, as the frames do.
 */
function intlLocale(locale: string): string {
  return locale === 'es' ? 'es-419' : 'en-US';
}

/** Minor units per major unit depend on the currency: JPY has none, USD has two. */
function minorDigits(currency: string): number {
  return (
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions()
      .maximumFractionDigits ?? 2
  );
}

export function formatMoney(amountMinor: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    trailingZeroDisplay: 'stripIfInteger',
  } as Intl.NumberFormatOptions).format(amountMinor / 10 ** minorDigits(currency));
}

/** "2 de noviembre de 2027" (E-18, E-20). */
export function formatLongDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'long' }).format(new Date(iso));
}

/** "9 de noviembre" (E-16, E-19). */
export function formatDayMonth(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { day: 'numeric', month: 'long' }).format(
    new Date(iso),
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** E-19 "Te quedan 5 días": a trial that ends in a few hours still has a day left. */
export function trialDaysLeft(trialEnd: string, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((Date.parse(trialEnd) - now) / DAY_MS));
}

function intervalPrice(product: ProductDTO, interval: PlanInterval): PriceDTO | undefined {
  return product.prices.find((price) => price.interval === interval);
}

/** The product of `kind: 'plan'` with recurring prices. Add-ons and vouchers never get here. */
export function selectPlanProduct(products: readonly ProductDTO[]): ProductDTO | null {
  return (
    products.find(
      (product) =>
        product.kind === 'plan' &&
        (intervalPrice(product, 'month') !== undefined ||
          intervalPrice(product, 'year') !== undefined),
    ) ?? null
  );
}

export function buildPlanOffer(products: readonly ProductDTO[], locale: string): PlanOffer | null {
  const product = selectPlanProduct(products);
  if (!product) return null;

  const toPlanPrice = (price: PriceDTO | undefined, interval: PlanInterval): PlanPrice | null =>
    price
      ? {
          priceId: price.id,
          interval,
          amountMinor: price.amountMinor,
          currency: price.currency,
          amount: formatMoney(price.amountMinor, price.currency, locale),
        }
      : null;

  const month = toPlanPrice(intervalPrice(product, 'month'), 'month');
  const year = toPlanPrice(intervalPrice(product, 'year'), 'year');

  let savingsPercent: number | null = null;
  if (month && year && month.currency === year.currency && month.amountMinor > 0) {
    const percent = Math.round((1 - year.amountMinor / (month.amountMinor * 12)) * 100);
    if (percent > 0) savingsPercent = percent;
  }

  const reference = year ?? month;
  if (!reference) return null;
  return {
    month,
    year,
    savingsPercent,
    monthlyEquivalent: year ? formatMoney(year.amountMinor / 12, year.currency, locale) : null,
    freeAmount: formatMoney(0, reference.currency, locale),
  };
}

export interface PlanOfferState {
  offer: PlanOffer | null;
  loading: boolean;
  /** `/billing/products` answered without a plan to sell: nothing went wrong, and retrying would not help. */
  unavailable: boolean;
  retry: () => void;
}

export function usePlanOffer(): PlanOfferState {
  const locale = useLocale();
  const plans = usePlans();
  const products = plans.data?.products;
  const offer = useMemo(
    () => (products ? buildPlanOffer(products, locale) : null),
    [products, locale],
  );
  const { refetch } = plans;
  const retry = useCallback(() => void refetch(), [refetch]);
  return {
    offer,
    loading: plans.isPending || (plans.isError && plans.isFetching),
    unavailable: plans.isSuccess && !offer,
    retry,
  };
}

/** Where Stripe brings the user back: this same page, without a previous result in the URL. */
export function checkoutReturnTo(
  current: Pick<Location, 'pathname' | 'search' | 'hash'> = location,
): string {
  const search = new URLSearchParams(current.search);
  search.delete('checkout');
  const query = search.toString();
  return `${current.pathname}${query ? `?${query}` : ''}${current.hash}`;
}

export type CheckoutParam = 'success' | 'cancel' | 'other';

export function parseCheckoutParam(search: Record<string, unknown>): CheckoutParam | null {
  const value = search.checkout;
  if (value === undefined) return null;
  return value === 'success' || value === 'cancel' ? value : 'other';
}

export const PREMIUM_POLL_TIMEOUT_MS = 30_000;
export const PREMIUM_POLL_INTERVAL_MS = 2_000;

/**
 * §5.8: the webhook can land after the browser does, so `GET /me` is asked until the plan shows up,
 * for 30 s at most. A failed request counts as "not yet": the payment is what is being waited on.
 */
export async function waitForPremium(
  isPremium: () => Promise<boolean>,
  {
    signal,
    timeoutMs = PREMIUM_POLL_TIMEOUT_MS,
    intervalMs = PREMIUM_POLL_INTERVAL_MS,
  }: { signal?: AbortSignal; timeoutMs?: number; intervalMs?: number } = {},
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    if (signal?.aborted) return false;
    if (await isPremium().catch(() => false)) return true;
    const left = deadline - Date.now();
    if (left <= 0 || signal?.aborted) return false;
    await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, left)));
  }
}

const CHECKOUT_SONG_KEY = 'akp.web.checkout.song';

/**
 * E-16 says "Cantar Bohemian Rhapsody" and E-17 reopens the paywall on the same song, after a full
 * page load. The intent only carries the id, so the song travels next to it for the round trip.
 */
export function rememberCheckoutSong(song: WebSong | undefined): void {
  try {
    if (song) sessionStorage.setItem(CHECKOUT_SONG_KEY, JSON.stringify(song));
    else sessionStorage.removeItem(CHECKOUT_SONG_KEY);
  } catch {
    /* private mode: E-16 falls back to the generic button */
  }
}

export function readCheckoutSong(): WebSong | undefined {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_SONG_KEY);
    return raw ? (JSON.parse(raw) as WebSong) : undefined;
  } catch {
    return undefined;
  }
}

/** What E-15 is showing; it lives here because the button that starts it can be on any screen. */
interface CheckoutUiState {
  redirectingSummary: string | null;
  setRedirecting(summary: string | null): void;
  /** The session the payment modal is showing. */
  embedded: EmbeddedSession | null;
  setEmbedded(session: EmbeddedSession | null): void;
  /** The session of the card modal: same form, but nothing is bought. */
  cardSession: EmbeddedSession | null;
  setCardSession(session: EmbeddedSession | null): void;
}

export interface EmbeddedSession {
  clientSecret: string;
  publishableKey: string;
}

export const useCheckoutUi = create<CheckoutUiState>((set) => ({
  redirectingSummary: null,
  setRedirecting: (redirectingSummary) => set({ redirectingSummary }),
  embedded: null,
  setEmbedded: (embedded) => set({ embedded }),
  cardSession: null,
  setCardSession: (cardSession) => set({ cardSession }),
}));

/** A payment is under way, in the modal or on its way to Stripe: the buttons that start one wait. */
export function usePaymentInProgress(): boolean {
  return useCheckoutUi(
    (s) => s.redirectingSummary !== null || s.embedded !== null || s.cardSession !== null,
  );
}

/** Where the portal's window lands when the person is done: it closes itself, or says it can. */
export const PORTAL_DONE_PATH = '/?portal=done';
const PORTAL_WINDOW_FEATURES = 'popup,width=1040,height=780';
const PORTAL_WATCH_MS = 1000;

/**
 * Stripe does not allow the portal in an iframe, so it gets a window of its own and this tab keeps
 * its page and its login. It has to open inside the click: after an `await`, browsers may block it.
 * A phone has no separate windows, and the browser makes it a tab.
 */
function openPortalWindow(): Window | null {
  const desktop =
    typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1024px)').matches;
  const popup = window.open('about:blank', '_blank', desktop ? PORTAL_WINDOW_FEATURES : undefined);
  if (!popup) return null;
  // Stripe's pages have no business reaching back into this tab.
  popup.opener = null;
  return popup;
}

/**
 * What the person changes there reaches `/me` through the webhook, so the plan is asked for again
 * when the window closes, and a moment later. A browser may also report it closed as soon as it
 * reaches Stripe, so coming back to this tab asks once more.
 */
function watchPortalWindow(popup: Window, refresh: () => void): void {
  const onFocus = () => {
    refresh();
    if (popup.closed) window.removeEventListener('focus', onFocus);
  };
  window.addEventListener('focus', onFocus);
  const timer = setInterval(() => {
    if (!popup.closed) return;
    clearInterval(timer);
    refresh();
    setTimeout(refresh, 3000);
  }, PORTAL_WATCH_MS);
}

function errorMessageKey(error: unknown) {
  if (error instanceof AkpError && error.code === 'rate_limited') {
    return 'web:common.errors.rateLimited' as const;
  }
  if (error instanceof AkpError && error.code === 'not_found') {
    return 'web:billing.errors.portalUnavailable' as const;
  }
  return 'web:common.errors.generic' as const;
}

export interface PayOptions {
  song?: WebSong | undefined;
  /** What to resume after creating the account, for a visitor (§5.1). */
  visitorIntent: () => Intent;
  /** E-06 → W-08 → W-05: the paywall comes back after the account exists. */
  paywallAfterAuth: boolean;
}

/**
 * The buttons that lead to Stripe. They return the text of a failure rather than toast it: inside a
 * modal `<dialog>` a toast would sit under the top layer, where nobody can read it.
 */
export function useCheckout() {
  const t = useWebT();
  const auth = useAuth();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const setPaywallAfterAuth = useWebUi((s) => s.setPaywallAfterAuth);
  const setRedirecting = useCheckoutUi((s) => s.setRedirecting);
  const setEmbedded = useCheckoutUi((s) => s.setEmbedded);
  const setCardSession = useCheckoutUi((s) => s.setCardSession);
  const cardModalEnabled = useCardModalEnabled();
  const queryClient = useQueryClient();
  // Without the public key the hosted page is the way to pay: nothing depends on it being set.
  const publishableKey = useStripeKey();
  const isAccount = auth.isAccount;

  /** `POST /billing/portal` (§5.8, 4), in a window of its own. */
  const openPortal = useCallback(async (): Promise<string | null> => {
    const popup = openPortalWindow();
    try {
      // Blocked: this tab is the only one left, so Stripe has to bring the person back to this page.
      const { url } = await webApi.portal({
        returnTo: popup ? PORTAL_DONE_PATH : checkoutReturnTo(),
      });
      if (!popup) {
        location.assign(url);
        return null;
      }
      popup.location.href = url;
      watchPortalWindow(popup, () => {
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      });
      return null;
    } catch (error) {
      popup?.close();
      return t(errorMessageKey(error));
    }
  }, [queryClient, t]);

  /** "Actualizar tarjeta": in the modal once the API offers it, and in the portal until then. */
  const updateCard = useCallback(async (): Promise<string | null> => {
    if (!cardModalEnabled || !publishableKey) return openPortal();
    try {
      const { clientSecret } = await webApi.cardSession();
      setCardSession({ clientSecret, publishableKey });
      return null;
    } catch (error) {
      return t(errorMessageKey(error));
    }
  }, [cardModalEnabled, openPortal, publishableKey, setCardSession, t]);

  const pay = useCallback(
    async (price: PlanPrice, options: PayOptions): Promise<string | null> => {
      if (!isAccount) {
        saveIntent(options.visitorIntent());
        setPaywallAfterAuth(options.paywallAfterAuth);
        openOverlay({ kind: 'requireAccount' });
        return null;
      }
      const compact = t(
        price.interval === 'year'
          ? 'web:billing.price.yearCompact'
          : 'web:billing.price.monthCompact',
        { amount: price.amount },
      );
      const plan = t(
        price.interval === 'year' ? 'web:billing.redirect.yearly' : 'web:billing.redirect.monthly',
        { price: compact },
      );
      const trial = TRIAL_OFFER_ENABLED ? ` · ${t('web:billing.trial.noteShort')}` : '';
      setRedirecting(`${plan}${trial}. ${t('web:billing.redirect.stripe')}`);
      rememberCheckoutSong(options.song);

      try {
        const session = await webApi.checkout({
          priceId: price.priceId,
          returnTo: checkoutReturnTo(),
          ...(publishableKey ? { embedded: true } : {}),
        });
        if ('clientSecret' in session) {
          setRedirecting(null);
          setEmbedded({ clientSecret: session.clientSecret, publishableKey });
          return null;
        }
        // E-15 stays up: the page is leaving.
        location.assign(session.url);
        return null;
      } catch (error) {
        if (error instanceof AkpError && error.code === 'already_subscribed') {
          const failure = await openPortal();
          // This page stays: the portal has its own window. E-15 has nothing left to announce.
          setRedirecting(null);
          return failure;
        }
        setRedirecting(null);
        return t(errorMessageKey(error));
      }
    },
    [
      isAccount,
      openOverlay,
      openPortal,
      publishableKey,
      setEmbedded,
      setPaywallAfterAuth,
      setRedirecting,
      t,
    ],
  );

  return { pay, openPortal, updateCard };
}
